import { Sparkles, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";

import { Tooltip } from "../../../components/ui/Tooltip";

/**
 * AI verdict on a contractor invoice document. Present in API responses only for internal
 * (GK ELEKTIM) users — the subcontractor never receives this field, so the badge simply
 * never renders on their side.
 */
export interface InvoiceAiAnalysis {
  status: "pending" | "running" | "ok" | "warning" | "error";
  status_label?: string;
  expected_pock_number?: string;
  expected_company?: string;
  expected_net?: string | null;
  expected_payment_term_days?: number | null;
  document_readable?: boolean;
  pock_number_found?: string;
  pock_number_ok?: boolean | null;
  buyer_company_found?: string;
  buyer_company_ok?: boolean | null;
  net_amount_found?: string | null;
  net_amount_ok?: boolean | null;
  payment_term_days_found?: number | null;
  payment_term_ok?: boolean | null;
  summary?: string;
  error_message?: string;
  analyzed_at?: string | null;
}

function check(ok: boolean | null | undefined): string {
  if (ok === true) return "✓";
  if (ok === false) return "✗";
  return "–";
}

function buildContent(a: InvoiceAiAnalysis): string {
  if (a.status === "pending") return "Analiza AI faktury w kolejce…";
  if (a.status === "running") return "Analiza AI faktury w toku…";
  if (a.status === "error") {
    return `Analiza AI nie powiodła się.\n${a.error_message || a.summary || "Nie udało się odczytać dokumentu."}`;
  }
  const lines: string[] = [];
  if (a.summary) lines.push(a.summary, "");
  lines.push(
    `${check(a.pock_number_ok)} Numer POCK` +
      (a.expected_pock_number ? ` (oczekiwany: ${a.expected_pock_number}` : "") +
      (a.pock_number_found ? `, na fakturze: ${a.pock_number_found})` : a.expected_pock_number ? ")" : ""),
  );
  lines.push(
    `${check(a.buyer_company_ok)} Nabywca` +
      (a.expected_company ? ` (oczekiwany: ${a.expected_company}` : "") +
      (a.buyer_company_found ? `, na fakturze: ${a.buyer_company_found})` : a.expected_company ? ")" : ""),
  );
  lines.push(
    `${check(a.net_amount_ok)} Kwota netto` +
      (a.expected_net ? ` (oczekiwana: ${a.expected_net}` : "") +
      (a.net_amount_found ? `, na fakturze: ${a.net_amount_found})` : a.expected_net ? ")" : ""),
  );
  // Payment term only when the Umowa Ramowa defines it.
  if (a.expected_payment_term_days != null) {
    lines.push(
      `${check(a.payment_term_ok)} Termin płatności (oczekiwany: ${a.expected_payment_term_days} dni` +
        (a.payment_term_days_found != null ? `, na fakturze: ${a.payment_term_days_found} dni)` : ")"),
    );
  }
  return lines.join("\n");
}

const STYLES: Record<InvoiceAiAnalysis["status"], { Icon: typeof Sparkles; cls: string; spin?: boolean }> = {
  ok: { Icon: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400" },
  warning: { Icon: AlertTriangle, cls: "text-amber-600 dark:text-amber-400" },
  error: { Icon: XCircle, cls: "text-red-600 dark:text-red-400" },
  pending: { Icon: Loader2, cls: "text-gray-400", spin: true },
  running: { Icon: Loader2, cls: "text-blue-500", spin: true },
};

/**
 * AI-check icon shown next to a contractor invoice for GK ELEKTIM users only. The analysis
 * runs on its own after the subcontractor uploads the invoice; the icon surfaces the ready
 * verdict (POCK number / GK company / net amount) on hover. Renders nothing when there is
 * no analysis (e.g. a contractor viewing their own invoice).
 */
export function InvoiceAiBadge({ analysis }: { analysis?: InvoiceAiAnalysis | null }) {
  if (!analysis) return null;
  const { Icon, cls, spin } = STYLES[analysis.status] ?? STYLES.warning;
  return (
    <Tooltip content={buildContent(analysis)}>
      <span className="inline-flex items-center gap-0.5 cursor-help align-middle" aria-label="Analiza AI faktury">
        <Sparkles className="w-3 h-3 text-purple-500" />
        <Icon className={`w-4 h-4 ${cls} ${spin ? "animate-spin" : ""}`} />
      </span>
    </Tooltip>
  );
}

/** Human-readable list of what the AI found wrong (failed or unverifiable checks). */
function problems(a: InvoiceAiAnalysis): string[] {
  const out: string[] = [];
  const expfound = (exp?: string | null, found?: string | null) =>
    (exp ? ` (oczekiwano: ${exp}` : "") +
    (found ? `${exp ? ", " : " ("}na fakturze: ${found})` : exp ? ")" : "");
  if (a.pock_number_ok === false || a.pock_number_ok == null)
    out.push(
      (a.pock_number_ok == null ? "Nie zweryfikowano numeru POCK" : "Brak / niezgodny numer POCK") +
        expfound(a.expected_pock_number, a.pock_number_found),
    );
  if (a.buyer_company_ok === false || a.buyer_company_ok == null)
    out.push(
      (a.buyer_company_ok == null ? "Nie zweryfikowano nabywcy" : "Niezgodny nabywca (spółka GK)") +
        expfound(a.expected_company, a.buyer_company_found),
    );
  if (a.net_amount_ok === false || a.net_amount_ok == null)
    out.push(
      (a.net_amount_ok == null ? "Nie zweryfikowano kwoty netto" : "Niezgodna kwota netto") +
        expfound(a.expected_net, a.net_amount_found),
    );
  if (a.expected_payment_term_days != null && a.payment_term_ok !== true)
    out.push(
      (a.payment_term_ok == null ? "Nie zweryfikowano terminu płatności" : "Niezgodny termin płatności") +
        expfound(
          `${a.expected_payment_term_days} dni`,
          a.payment_term_days_found != null ? `${a.payment_term_days_found} dni` : null,
        ),
    );
  return out;
}

/**
 * Inline red alert shown directly under an invoice when the AI check found a problem
 * (error or warning). Internal (GK) users only. Returns null when the analysis is missing,
 * still running, or fully OK.
 */
export function InvoiceAiInlineAlert({ analysis }: { analysis?: InvoiceAiAnalysis | null }) {
  if (!analysis) return null;
  if (analysis.status !== "error" && analysis.status !== "warning") return null;

  const items = analysis.status === "warning" ? problems(analysis) : [];
  const errorText =
    analysis.status === "error"
      ? analysis.error_message || analysis.summary || "Nie udało się odczytać dokumentu jako faktury."
      : "";

  return (
    <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Sparkles className="w-3.5 h-3.5 text-purple-500" />
        <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
        <span className="text-xs font-semibold text-red-800 dark:text-red-300">
          Analiza AI: {analysis.status === "error" ? "nie udało się zweryfikować faktury" : "faktura wymaga uwagi"}
        </span>
      </div>
      {analysis.status === "warning" && analysis.summary && (
        <p className="text-xs text-red-700 dark:text-red-300 mb-1">{analysis.summary}</p>
      )}
      {errorText && <p className="text-sm text-red-700 dark:text-red-300">{errorText}</p>}
      {items.length > 0 && (
        <ul className="list-disc pl-5 space-y-0.5 text-sm text-red-700 dark:text-red-300">
          {items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, ok, expected, found }: { label: string; ok?: boolean | null; expected?: string | null; found?: string | null }) {
  const okStyle = ok === true ? STYLES.ok : ok === false ? STYLES.error : STYLES.warning;
  const { Icon, cls } = okStyle;
  return (
    <li className="flex items-start gap-2 py-1.5">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cls}`} />
      <div className="min-w-0">
        <span className="font-medium text-gray-800 dark:text-gray-200">{label}</span>
        {(expected || found) && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {expected && <>oczekiwane: <span className="font-mono">{expected}</span></>}
            {expected && found && " · "}
            {found && <>na fakturze: <span className="font-mono">{found}</span></>}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Full AI-verdict card for the invoice detail page. Internal (GK) users only — never
 * rendered for contractors, since the API omits ``ai_analysis`` for them.
 */
export function InvoiceAiPanel({ analysis }: { analysis?: InvoiceAiAnalysis | null }) {
  if (!analysis) return null;
  const { Icon, cls, spin } = STYLES[analysis.status] ?? STYLES.warning;
  const busy = analysis.status === "pending" || analysis.status === "running";
  return (
    <div className="rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-50/40 dark:bg-purple-900/10 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-purple-500" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">Analiza AI faktury</span>
        <Icon className={`w-4 h-4 ${cls} ${spin ? "animate-spin" : ""}`} />
        <span className={`text-sm ${cls}`}>{analysis.status_label}</span>
      </div>
      {busy ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Analiza uruchamia się automatycznie po przesłaniu faktury…
        </p>
      ) : analysis.status === "error" ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          {analysis.error_message || analysis.summary || "Nie udało się przeanalizować dokumentu."}
        </p>
      ) : (
        <>
          {analysis.summary && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{analysis.summary}</p>
          )}
          <ul className="divide-y divide-purple-100 dark:divide-purple-900/40">
            <Row label="Numer POCK na fakturze" ok={analysis.pock_number_ok}
              expected={analysis.expected_pock_number} found={analysis.pock_number_found} />
            <Row label="Spółka GK (nabywca)" ok={analysis.buyer_company_ok}
              expected={analysis.expected_company} found={analysis.buyer_company_found} />
            <Row label="Kwota netto" ok={analysis.net_amount_ok}
              expected={analysis.expected_net ?? undefined} found={analysis.net_amount_found ?? undefined} />
            {analysis.expected_payment_term_days != null && (
              <Row label="Termin płatności (umowa ramowa)" ok={analysis.payment_term_ok}
                expected={`${analysis.expected_payment_term_days} dni`}
                found={analysis.payment_term_days_found != null ? `${analysis.payment_term_days_found} dni` : undefined} />
            )}
          </ul>
        </>
      )}
    </div>
  );
}
